import type { TimelyAnalysis } from '@trainme/db/client';
import { TimePeriod } from '@/utils/prismaEnums';
import { endOfDay, startOfDay } from 'date-fns';
import { create } from 'zustand';
import { createMapArrayCache } from './mapArrayCache';

interface TimelyAnalysisState {
  timelyAnalyses: Map<string, TimelyAnalysis>;
  lastSyncAt: number;
  mergeTimelyAnalyses: (newAnalyses: TimelyAnalysis[]) => void;
  getTimelyAnalysis: (
    period: TimePeriod,
    startDate: Date
  ) => TimelyAnalysis | undefined;
  clearTimelyAnalyses: () => void;
  getFilteredTimelyAnalyses: (range?: {
    start: Date;
    end: Date;
    period?: TimePeriod;
  }) => TimelyAnalysis[];
}

// Key format: period-YYYY-MM-DD (UTC)
const getUtcDayKey = (date: Date) => {
  const utcDay = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  return utcDay.toISOString();
};

const getAnalysisKey = (period: TimePeriod, startDate: Date): string => {
  return `${period}-${getUtcDayKey(startDate)}`;
};

const getTimelyAnalysesArray = createMapArrayCache<string, TimelyAnalysis>();

export const useTimelyAnalysisStore = create<TimelyAnalysisState>(
  (set, get) => ({
    timelyAnalyses: new Map(),
    lastSyncAt: 0,

    mergeTimelyAnalyses: (newAnalyses) => {
      set((state) => {
        const updatedMap = new Map(state.timelyAnalyses);
        newAnalyses.forEach((analysis) => {
          updatedMap.set(
            getAnalysisKey(analysis.period, analysis.startDate),
            analysis
          );
        });
        return { timelyAnalyses: updatedMap };
      });
    },

    getTimelyAnalysis: (period, startDate) => {
      return get().timelyAnalyses.get(getAnalysisKey(period, startDate));
    },

    clearTimelyAnalyses: () => {
      set({ timelyAnalyses: new Map(), lastSyncAt: Date.now() });
    },

    getFilteredTimelyAnalyses: (range) => {
      const analyses = getTimelyAnalysesArray(get().timelyAnalyses);

      if (!range) {
        return analyses;
      }

      const rangeStart = startOfDay(range.start);
      const rangeEnd = endOfDay(range.end);

      return analyses.filter((analysis) => {
        const analysisDate = new Date(analysis.startDate);
        const isWithinRange =
          analysisDate >= rangeStart && analysisDate <= rangeEnd;
        const isCorrectPeriod = range.period
          ? analysis.period === range.period
          : true;
        return isWithinRange && isCorrectPeriod;
      });
    },
  })
);
